import {
  CategoryOutlined,
  FactCheckOutlined,
  FormatQuoteOutlined,
} from "@mui/icons-material";
import { Dispatch, SetStateAction, useEffect, useMemo, useRef } from "react";
import { useUIStore } from "src/views/assistant/stores/UIStore";
import SourcesTab from "src/reusable/Sidebar/tabs/SourcesTab/SourcesTab";
import ArtifactsTab from "src/reusable/Sidebar/tabs/ArtifactsTab/ArtifactsTab";
import AutoOpenToggle from "src/reusable/Sidebar/AutoOpenToggle";
import SourcesDownloadButton from "src/views/assistant/components/sources/SourcesDownloadButton";
import SourcesViewToggle from "src/views/assistant/components/sources/SourcesViewToggle";
import FfnRecordPanel from "src/views/assistant/components/sidebar/FfnRecordPanel";
import { useFfnRecordStore } from "src/views/assistant/stores/FfnRecordStore";
import { SidebarConfig } from "src/reusable/Sidebar/types";

export interface FfnSidebarContext {
  dialogueId: number | undefined;
  responseSources: any[];
  artifacts: Record<string, any>;
  viewMode: "dataChunk" | "cardList";
  setViewMode: Dispatch<SetStateAction<"dataChunk" | "cardList">>;
}

/**
 * Sidebar config for the 50.59 chat page: the record panel (same
 * FfnRecordPanel the Atom Assist sidebar mounts, fed by tool responses
 * carrying panel_type "ffn_record"), plus the shared sources/artifacts tabs.
 */
export const useFfnSidebar = (
  _context: FfnSidebarContext,
): SidebarConfig<FfnSidebarContext> => {
  const { preferences, setRightSidebar } = useUIStore();
  const autoOpenEnabled = preferences?.autoOpenSidebar ?? true;
  const ffnRecord = useFfnRecordStore((s) => s.record);
  const hasFfnRecord = ffnRecord !== null;
  const ffnBadge = hasFfnRecord
    ? (ffnRecord?.challenge_failed_items?.length ?? 0) || 1
    : 0;

  // When the record first appears (and auto-open is on), widen the sidebar to
  // half the screen — the record panel is a working surface, not a peek.
  // Same behavior as the Atom Assist sidebar's ffn-record tab.
  const prevHasFfnRecord = useRef(false);
  useEffect(() => {
    if (hasFfnRecord && !prevHasFfnRecord.current && autoOpenEnabled) {
      setRightSidebar({
        width: Math.max(500, Math.floor(window.innerWidth / 2)),
        isOpen: true,
        selectedItem: "ffn-record",
      });
    }
    prevHasFfnRecord.current = hasFfnRecord;
  }, [hasFfnRecord, autoOpenEnabled, setRightSidebar]);

  return useMemo(
    () => ({
      tabs: [
        ...(hasFfnRecord
          ? [
              {
                id: "ffn-record" as const,
                label: "50.59",
                headerLabel: "50.59 Record",
                icon: FactCheckOutlined,
                renderContent: () => <FfnRecordPanel />,
                getBadgeContent: () => ffnBadge,
              },
            ]
          : []),
        {
          id: "sources",
          label: "Sources",
          icon: FormatQuoteOutlined,
          renderContent: ({ context: ctx }) => {
            if (!ctx) return null;
            return (
              <SourcesTab
                sources={ctx.responseSources}
                viewMode={ctx.viewMode}
                setViewMode={ctx.setViewMode}
                detailSourceViewSdrId={null}
                detailSourceViewSourceId={null}
                setDetailSourceViewSdrId={() => {}}
                setDetailSourceViewSourceId={() => {}}
                // Three-tier provenance funnel (#20423 Story 4): without the
                // dialogue id SourcesTab silently falls back to the legacy list.
                dialogueId={ctx.dialogueId ?? null}
              />
            );
          },
          renderHeaderActions: ({ context: ctx }) => {
            if (!ctx) return null;
            return (
              <>
                <SourcesDownloadButton responseSources={ctx.responseSources} />
                <SourcesViewToggle
                  alignment={ctx.viewMode}
                  handleAlignment={(_, v) => {
                    if (v) ctx.setViewMode(v as "dataChunk" | "cardList");
                  }}
                />
                <AutoOpenToggle />
              </>
            );
          },
          getBadgeContent: (ctx) => ctx?.responseSources.length ?? 0,
        },
        {
          id: "artifacts",
          label: "Artifacts",
          icon: CategoryOutlined,
          renderContent: ({ context: ctx }) => {
            if (!ctx) return null;
            return <ArtifactsTab artifacts={ctx.artifacts} />;
          },
          renderHeaderActions: () => <AutoOpenToggle />,
          getBadgeContent: (ctx) =>
            ctx?.artifacts ? Object.keys(ctx.artifacts).length : 0,
        },
      ],
      defaultTab: hasFfnRecord ? "ffn-record" : "sources",
      responsive: {
        enableMobile: true,
        enableDesktopNav: true,
        enableResize: true,
        minWidth: 300,
        maxWidth: 800,
        defaultWidth: 400,
      },
      autoOpen: {
        enabled: () => preferences?.autoOpenSidebar ?? true,
        getContentCounts: (ctx) => ({
          sources: ctx?.responseSources.length ?? 0,
          artifacts: ctx?.artifacts ? Object.keys(ctx.artifacts).length : 0,
        }),
        autoSelectTab: "sources",
        triggerKey: "sources",
      },
    }),
    [hasFfnRecord, ffnBadge, preferences?.autoOpenSidebar],
  );
};
